import { type FC } from "react";
import {
  Autocomplete,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useContactListsQuery,
  useMarketingLimits,
  type AudienceEditorProps,
} from "@/partner/entities/marketing";
import { AudienceFilterBuilder } from "./AudienceFilterBuilder";

interface ContactListOption {
  id: string;
  name: string;
  contactsCount: number;
}

/**
 * Аудитория: зрители платформы по фильтру и списки контактов. Общая для
 * рассылки и события автоматизации, подставляется страницей через
 * MarketingComponentsProvider. allowContactLists=false (цепочка, правило
 * сервера contactLists): только фильтр зрителей, без переключателя и без
 * списков контактов.
 */
export const AudienceEditor: FC<AudienceEditorProps> = ({
  value,
  onChange,
  disabled,
  estimate,
  allowContactLists = true,
}) => {
  const { t } = useTranslation();
  // Пока пределов нет (запрос не ответил или упал), число списков не
  // ограничиваем: лишнее отклонит бэкенд при сохранении.
  const limits = useMarketingLimits();
  const listsMax = limits?.contactListsPerAudience ?? null;
  const { data, loading } = useContactListsQuery({
    variables: { page: 0, rowsPerPage: 100 },
    fetchPolicy: "cache-and-network",
    skip: !allowContactLists,
  });
  const options: ContactListOption[] = (data?.contactLists.nodes ?? []).map(
    (node) => ({
      id: node.id,
      name: node.name,
      contactsCount: node.contactsCount,
    })
  );
  const selected = options.filter((o) => value.contactListIds.includes(o.id));
  const atListsLimit = listsMax !== null && selected.length >= listsMax;
  const listsLimitHint = atListsLimit
    ? t("broadcasts.audience.listsLimitAny", { count: listsMax })
    : null;

  return (
    <Stack spacing={2}>
      {allowContactLists && (
        <FormControlLabel
          control={
            <Switch
              checked={value.viewersEnabled}
              onChange={(event) =>
                onChange({ viewersEnabled: event.target.checked })
              }
              disabled={disabled}
            />
          }
          label={t("broadcasts.audience.viewers")}
        />
      )}
      {(value.viewersEnabled || !allowContactLists) && (
        <AudienceFilterBuilder
          value={value.audience}
          // Цепочка: переключателя нет, фильтр всегда включает зрителей,
          // иначе форма отправила бы audience: null и условия пропали бы.
          onChange={(audience) =>
            onChange(
              allowContactLists
                ? { audience }
                : { audience, viewersEnabled: true }
            )
          }
          disabled={disabled}
          limits={limits}
        />
      )}
      {allowContactLists && (
        <Autocomplete<ContactListOption, true>
          multiple
          fullWidth
          disabled={disabled}
          loading={loading}
          options={options}
          getOptionLabel={(option) =>
            t("broadcasts.audience.listOption", {
              name: option.name,
              count: option.contactsCount,
            })
          }
          isOptionEqualToValue={(option, current) => option.id === current.id}
          value={selected}
          getOptionDisabled={(option) =>
            atListsLimit && !selected.some((v) => v.id === option.id)
          }
          onChange={(_event, next) => {
            if (listsMax !== null && next.length > listsMax) return;
            onChange({ contactListIds: next.map((o) => o.id) });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("broadcasts.audience.lists")}
              helperText={listsLimitHint}
            />
          )}
        />
      )}
      {estimate !== null && (
        <Stack spacing={0.5}>
          <Typography variant="body2">
            {t("broadcasts.audience.estimate", { count: estimate })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("broadcasts.audience.estimateHint")}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};
